import React from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Avatar } from '@parsina/framework';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { statusColors, CT, soft } from '@parsina/framework/tokens';
import { Clock, StickyNote } from 'lucide-react';
import { waitedMinutes, visitOverrun } from '../hooks/useReception';
import { URGENCY_BADGE, WAIT_WARN_MIN, formatDuration, isDone, isReadOnly, isOwnPendingRequest } from '../lib/appointments';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../lib/receptionStrings';
import { RowOpsMenu } from './RowOpsMenu';
import PatientPhones from './PatientPhones';

// The DESKTOP appointment row for the web پذیرش screen's day blocks. The mobile
// surface renders its own `MobileAppointmentCard` (src/components/mobile/) — the
// two share their vocabulary and clocks through lib/appointments and the
// پذیرش day-model, never their markup (DESIGN-CONCEPT-MOBILE v2 §1.1).

export { isDone, formatDuration, URGENCY_BADGE };

// One escalation vocabulary for an open visit: normal → quiet (in-visit blue),
// over (past its slot length) → amber, critical (past 2×) → red. All from tokens.
const OVERRUN_CHIP = {
  normal:   { bg: statusColors.in_visit.bg, color: statusColors.in_visit.text },
  over:     { bg: C.amberLight, color: CT.warnInk },
  critical: { bg: C.redLight,  color: CT.dangerInk },
};

// Neutral chip surface (no #f1f5f9 token exists; CT.surface2 is the token neutral).
const NEUTRAL_BG = CT.surface2;

// A clock glyph on the text baseline inside a chip — replaces the ⏱ emoji.
const ClockGlyph = () => (
  <Clock size={11} strokeWidth={2.4} style={{ display: 'inline-block', verticalAlign: '-1.5px', marginInlineEnd: 3 }} />
);

const posLabel = (item, pos) => (pos === 1
  ? item('dashboard.queue.positionNext')
  : item('dashboard.queue.position', { position: pos }));

// ── Queue position badge ──────────────────────────────────────────────────────
const QueuePos = ({ pos }) => {
  const { t } = useTranslation();
  return (
  <span style={{
    fontSize: '0.625rem', fontWeight: 800, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap',
    color: pos === 1 ? statusColors.arrived.text : C.textSecondary,
    background: pos === 1 ? statusColors.arrived.bg : NEUTRAL_BG,
    border: `1px solid ${pos === 1 ? 'transparent' : C.border}`,
    flexShrink: 0,
  }}>
    {posLabel(t, pos)}
  </span>
  );
};

// ── Row (web) ─────────────────────────────────────────────────────────────────
// `clinicName` is passed ONLY while پذیرش is showing every clinic at once. With
// one clinic on screen the tag would repeat the header chip on every row, which
// is noise; with several it is the one thing that says whose desk booked this
// patient.
// `ops` (PM-0518) — the row's further ops from `useReceptionRowOps().opsFor`,
// behind one ⋮ menu at the row's end.
const AppointmentRow = ({ apt, queuePos, nowMs, onAction, onArrived, onWithdraw, onEdit, arriving = false, clinicName, ops = [] }) => {
  const { t } = useTranslation();
  const s = useReceptionStrings();
  const [hovered, setHovered] = React.useState(false);

  const waited = apt.status === 'arrived' ? waitedMinutes(apt, nowMs) : null;
  const { elapsedMin, level } = apt.status === 'in_visit'
    ? visitOverrun(apt, nowMs)
    : { elapsedMin: null, level: 'normal' };
  const urg = URGENCY_BADGE[apt.urgency];
  const overChip = OVERRUN_CHIP[level];
  // The decorative leading dot is tinted from the one status source — no local palette.
  const dotColor = (statusColors[apt.status] || {}).text || C.textMuted;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        borderRadius: 12,
        marginBottom: 8,
        background: hovered ? CT.surface2 : 'white',
        border: `1.5px solid ${hovered ? C.border : NEUTRAL_BG}`,
        transition: 'all 0.15s',
      }}
    >
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />

      <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary, minWidth: 44, textAlign: 'center', flexShrink: 0 }}>
        {apt.time || '—'}
      </div>

      <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />

      <Avatar name={apt.name} size={32} bg={C.navy} />

      {/* The patient's name never collapses (PM-0212): the chips beside it are
          rigid, and in a language with longer chip text (German) they squeezed
          the name to 0 px — a doctor must always see whose row they start. The
          clinic chip gives way first; the name keeps at least 64 px. */}
      <div style={{ flex: 1, minWidth: 120, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, minWidth: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {apt.name || t('dashboard.hero.unknownPatient')}
        </div>
        {apt.patient?.phone && (
          <span style={{ fontSize: '0.6875rem', color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
            {/* The main number in the page's digits, as an LTR island, and «+N» when
                the record holds more (PM-0536). The kit's read path is landline-
                tolerant and hands back the raw value when it cannot parse one, so this
                never blanks. The row already gated on the column being present. */}
            <PatientPhones patient={apt.patient} data-testid={`reception-row-${apt.id}-phones`} />
          </span>
        )}
        {urg && (
          <span style={{
            fontSize: '0.59375rem', fontWeight: 800, borderRadius: 5, padding: '1px 6px', flexShrink: 0,
            background: urg.bg, color: urg.color,
          }}>
            {t(urg.labelKey)}
          </span>
        )}
        {apt.type === 'online' && (
          <span
            data-testid={`reception-row-${apt.id}-online`}
            style={{
              fontSize: '0.59375rem', fontWeight: 800, borderRadius: 5, padding: '1px 6px', flexShrink: 0,
              background: C.tealLight, color: C.navyDark,
            }}
          >
            {s('dashboard.edit.online')}
          </span>
        )}
        {clinicName && (
          <span
            data-testid="reception-row-clinic"
            title={clinicName}
            style={{
              fontSize: '0.59375rem', fontWeight: 700, borderRadius: 5, padding: '1px 6px',
              flexShrink: 1, minWidth: 32, maxWidth: 120, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              background: soft(C.navy, 0.08), color: C.navyDark,
            }}
          >
            {clinicName}
          </span>
        )}
      </div>
      {/* The desk writes it as «توضیحات برای پزشک» — this is that reader (PM-0524).
          One line; the whole note is the tooltip and the ⋮ «یادداشت نوبت». */}
      {apt.reminderNote && (
        <div
          data-testid={`reception-row-${apt.id}-note`}
          title={apt.reminderNote}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, minWidth: 0,
            fontSize: '0.71875rem', color: C.textSecondary,
          }}
        >
          <StickyNote size={12} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <bdi>{apt.reminderNote}</bdi>
          </span>
        </div>
      )}
      </div>

      {waited != null && (
        // The short form beside the clock glyph, as on the phone card: the long
        // «wartet seit … Min.» crowded the name out of the row (PM-0212). The
        // long form stays the chip's tooltip.
        <span title={t('dashboard.queue.waitedMinutes', { count: waited })} style={{
          fontSize: '0.65625rem', fontWeight: 700, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0,
          background: waited > WAIT_WARN_MIN ? C.amberLight : NEUTRAL_BG,
          color: waited > WAIT_WARN_MIN ? CT.warnInk : C.textSecondary,
        }}>
          <ClockGlyph />{t('dashboard.queue.waitedShort', { count: waited })}
        </span>
      )}

      {elapsedMin != null && (
        <span style={{
          fontSize: '0.65625rem', fontWeight: 700, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0,
          background: overChip.bg, color: overChip.color,
        }}>
          <ClockGlyph />{formatDuration(t, elapsedMin)}
        </span>
      )}

      {queuePos != null && <QueuePos pos={queuePos} />}

      {/* StatusChip, not the framework's StatusBadge: `statusColors` ships ONE
          Persian label per status for every app that consumes the package, so a
          StatusBadge printed «نوبت‌دار» at an English or Arabic panel. Same map,
          same colours, same pill — the label just goes through `t` first. */}
      <StatusChip status={apt.status} style={{ flexShrink: 0, whiteSpace: 'nowrap' }} />

      {apt.status === 'scheduled' && onArrived && (
        <button type="button" disabled={arriving} aria-busy={arriving || undefined}
          data-testid={`reception-row-${apt.id}-arrive`}
          onClick={e => { e.stopPropagation(); onArrived(); }} style={{
          height: 30, padding: '0 12px',
          background: statusColors.arrived.bg, color: statusColors.arrived.text,
          border: 'none', borderRadius: 8,
          fontSize: '0.75rem', fontWeight: 700, cursor: arriving ? 'progress' : 'pointer', flexShrink: 0,
          opacity: arriving ? 0.6 : 1,
        }}>
          {t('dashboard.queue.arrived')}
        </button>
      )}

      {/* «ویرایش نوبت» (PM-0102, PM-0531) — quiet, between «ثبت حضور» and «شروع»: the
          row's first acts stay the patient arriving and the visit starting. A waiting
          row carries it as a button; once the patient is here it moves into the ⋮
          menu (receptionRowOps `edit`). The caller passes it only for a row the
          doctor may edit. */}
      {apt.status === 'scheduled' && onEdit && (
        <button type="button"
          data-testid={`reception-row-${apt.id}-edit`}
          onClick={e => { e.stopPropagation(); onEdit(); }} style={{
          height: 30, padding: '0 12px',
          background: 'transparent', color: C.textSecondary,
          border: `1px solid ${C.border}`, borderRadius: 8,
          fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
        }}>
          {s('dashboard.edit.action')}
        </button>
      )}

      {/* A request the clinic has not confirmed is READ-ONLY here: starting a
          visit on it would put a patient in the room the desk never booked, and
          scheduling refuses the transition anyway. The chip above already says
          «در انتظار تأیید کلینیک»; no button follows it. */}
      {/* …except the doctor's OWN booking awaiting the clinic, which they may
          withdraw. Confirming it stays the desk's. */}
      {isOwnPendingRequest(apt) && onWithdraw && (
        <button type="button"
          data-testid={`reception-row-${apt.id}-withdraw`}
          onClick={e => { e.stopPropagation(); onWithdraw(); }} style={{
          height: 30, padding: '0 12px',
          background: 'transparent', color: CT.dangerInk,
          border: `1px solid ${C.border}`, borderRadius: 8,
          fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
        }}>
          {s('dashboard.withdraw.action')}
        </button>
      )}

      {!isDone(apt.status) && !isReadOnly(apt.status) && (
        <button onClick={e => { e.stopPropagation(); onAction(); }} style={{
          height: 30, padding: '0 12px',
          background: NEUTRAL_BG, color: C.navy,
          border: 'none', borderRadius: 8,
          fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          transition: 'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = CT.border2}
          onMouseLeave={e => e.currentTarget.style.background = NEUTRAL_BG}
        >
          {apt.status === 'in_visit' ? t('dashboard.queue.continue') : t('dashboard.queue.start')}
        </button>
      )}

      <RowOpsMenu
        ops={ops}
        label={s('dashboard.rowOps.more', { name: apt.name || t('common.labels.patient') })}
        testId={`reception-row-${apt.id}`}
      />
    </div>
  );
};

export default AppointmentRow;
